import { type FC } from 'react';
import Button from '@components/Button';
import Icon from '@components/Icon';
import InputText from '@components/InputText';
import type { EvalWorkflowType, GuidelinePack } from '@root/domain/schemas/evals/eval-config.schema';
import { v4 as uuidv4 } from 'uuid';

import { cardBodyClasses, cardClasses, cardHeaderClasses, sectionHeadingClasses, subtleTextClasses } from '../classes';
import { getWorkflowPackExpectations } from '../constants';

import { MarkdownEditor } from './MarkdownEditor';

interface GuidelinePacksTabProps {
  workflowType: EvalWorkflowType;
  guidelinePacks: GuidelinePack[];
  isDirty: boolean;
  isSaving: boolean;
  onChange: (packs: GuidelinePack[]) => void;
  onSave: () => void;
}

const createEmptyPack = (): GuidelinePack => ({
  id: uuidv4(),
  name: '',
  description: '',
  source_document: '',
  content_markdown: '',
});

export const GuidelinePacksTab: FC<GuidelinePacksTabProps> = ({
  workflowType,
  guidelinePacks,
  isDirty,
  isSaving,
  onChange,
  onSave,
}) => {
  const expectations = getWorkflowPackExpectations(workflowType);

  const updatePack = (id: string, patch: Partial<GuidelinePack>) => {
    onChange(guidelinePacks.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };
  const removePack = (id: string) => onChange(guidelinePacks.filter((p) => p.id !== id));
  const addPack = () => onChange([...guidelinePacks, createEmptyPack()]);

  const isBelowMinimum = guidelinePacks.length < expectations.expectedGuidelineCount.min;

  return (
    <div className={cardClasses}>
      <div className={cardHeaderClasses}>
        <div className="flex min-w-0 flex-col">
          <h2 className={sectionHeadingClasses}>Guideline packs</h2>
          <p className={subtleTextClasses}>{expectations.expectedGuidelineCount.description}</p>
        </div>
        <Button
          kind="primary"
          onClick={onSave}
          isDisabled={!isDirty || isSaving}
          loading={isSaving}
          className="h-9.5 shrink-0 whitespace-nowrap"
          data-testid="button:save-guideline-packs"
        >
          Save draft
        </Button>
      </div>
      <div className={cardBodyClasses}>
        {isBelowMinimum && (
          <div className="text-warning-700 border-warning-200 bg-warning-50 rounded-md border px-3 py-2 text-xs">
            Below the minimum of {expectations.expectedGuidelineCount.min} guideline pack(s) for this workflow type.
          </div>
        )}

        {guidelinePacks.length === 0 && (
          <p className={subtleTextClasses}>
            No guideline packs yet. Paste a Markdown excerpt from a source document to start.
          </p>
        )}

        <div className="flex flex-col gap-4">
          {guidelinePacks.map((pack, index) => (
            <div
              key={pack.id}
              className="border-info-200 flex flex-col gap-3 rounded-md border p-4"
              data-testid={`guideline-pack:${index}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-info-700 text-xs font-medium">Pack {index + 1}</div>
                <button
                  type="button"
                  className="text-info-400 hover:text-error-600 cursor-pointer transition-colors"
                  onClick={() => removePack(pack.id)}
                  aria-label={`Remove guideline pack ${index + 1}`}
                  data-testid={`button:remove-guideline-pack-${index}`}
                >
                  <Icon name="cross" className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <InputText
                  labelText="Name"
                  value={pack.name}
                  isRequired
                  onChange={(e) => updatePack(pack.id, { name: e.target.value })}
                />
                <InputText
                  labelText="Source document"
                  value={pack.source_document ?? ''}
                  descriptionText="e.g. MRCv3 Guidance - March 2021.pdf"
                  onChange={(e) => updatePack(pack.id, { source_document: e.target.value })}
                />
              </div>
              <InputText
                labelText="Description"
                value={pack.description ?? ''}
                onChange={(e) => updatePack(pack.id, { description: e.target.value })}
              />
              <MarkdownEditor
                labelText="Content (Markdown)"
                value={pack.content_markdown}
                onChange={(content_markdown) => updatePack(pack.id, { content_markdown })}
                placeholder="Paste a markdown excerpt from the source document..."
                minRows={18}
                dataTestId={`guideline-pack-${index}:content`}
                name={`guideline-pack-${index}-content`}
              />
            </div>
          ))}
        </div>

        <div>
          <Button onClick={addPack} className="h-9.5" data-testid="button:add-guideline-pack">
            Add guideline pack
          </Button>
        </div>
      </div>
    </div>
  );
};
